/*
 * Common Modal displays the modal
 */

import * as React from 'react';
import { Modal } from 'react-bootstrap';

export interface IPropsCommonModal extends React.Props<CommonModal> {
    show: boolean;
    onHide: () => void;
    onEntered?: () => void;
    className: string;
    footer?: JSX.Element;
    title: string | JSX.Element;
    container?: any;
    backdrop?: string | boolean;
    wideModal?: boolean;
    secondModal?: boolean;
    secondWideModal?: boolean; // used when first modal is wide and this is a second modal
}

interface State {
    classNameAnimation: string;
    renderDelayActive: boolean; // improves performance and resolves an issue where a modal crashes on app launch when it is being shown and then immediately hidden
}

class CommonModal extends React.Component<IPropsCommonModal, State> {
    private secondModalEl: HTMLElement | null = null;
    private wheelForwardHandler: ((e: WheelEvent) => void) | null = null;
    private touchStartHandler: ((e: TouchEvent) => void) | null = null;
    private touchMoveHandler: ((e: TouchEvent) => void) | null = null;
    private touchStartY = 0;

    constructor(props: IPropsCommonModal) {
        super(props);
        this.state = {
            classNameAnimation: '',
            renderDelayActive: true
        };
    }

    componentDidMount() {
        setTimeout(() => {
            this.setState({ renderDelayActive: false });
        }, 1000);
    }

    componentWillUnmount() {
        this.detachScrollForwarder();
    }

    /*
     * MMG-3939: The second modal wrapper is full-screen (Bootstrap default) and reaches
     * z-index 1055 after its open animation, putting it above the first modal (z-index 1050).
     * Scroll events (wheel on desktop, touchmove on iOS) land on the second modal wrapper,
     * which has overflow:hidden — and because scroll events only propagate to DOM ancestors
     * (not z-index siblings), the first modal body never receives them.
     *
     * Fix: attach native wheel + touch listeners to the second modal wrapper. When the
     * interaction is outside the dialog area (the transparent full-screen portion), forward
     * the scroll delta to the first modal body. Click/tap events are untouched, so
     * Bootstrap's backdrop-tap-to-close continues to close the correct (second) modal.
     */
    attachScrollForwarder = () => {
        // MMG-4381: each armed instance targets its own wrapper class; a
        // second-wide modal only arms when no plain second modal is open
        // (see onEntered), so mixed stacks keep their original behavior
        const secondModalEl = document.querySelector<HTMLElement>(
            this.props.secondModal
                ? '.second-modal.modal'
                : '.second-wide-modal.modal'
        );
        if (!secondModalEl || this.secondModalEl === secondModalEl) return;

        this.secondModalEl = secondModalEl;

        const isOutsideDialog = (target: EventTarget | null) => {
            const dialog = secondModalEl.querySelector('.modal-dialog');
            return !dialog || !dialog.contains(target as Node);
        };

        // first open modal body that isn't inside this wrapper
        const getFirstModalBody = () => {
            const bodies = document.querySelectorAll<HTMLElement>(
                '.modal .modal-body'
            );
            for (let i = 0; i < bodies.length; i++) {
                const body = bodies[i];
                if (body && !secondModalEl.contains(body)) {
                    return body;
                }
            }
            return null;
        };

        // Desktop / macOS trackpad
        this.wheelForwardHandler = (e: WheelEvent) => {
            if (!isOutsideDialog(e.target)) return;
            const firstModalBody = getFirstModalBody();
            if (firstModalBody) {
                e.preventDefault();
                let delta = e.deltaY;
                if (e.deltaMode === 1) delta *= 20; // LINE mode
                if (e.deltaMode === 2) delta *= 400; // PAGE mode
                firstModalBody.scrollTop += delta;
            }
        };

        // iOS / touch: record start position
        this.touchStartHandler = (e: TouchEvent) => {
            if (!isOutsideDialog(e.target)) return;
            this.touchStartY = e.touches[0].clientY;
        };

        // iOS / touch: forward delta on each move
        this.touchMoveHandler = (e: TouchEvent) => {
            if (!isOutsideDialog(e.target)) return;
            const firstModalBody = getFirstModalBody();
            if (firstModalBody) {
                e.preventDefault();
                const currentY = e.touches[0].clientY;
                const delta = this.touchStartY - currentY;
                this.touchStartY = currentY;
                firstModalBody.scrollTop += delta;
            }
        };

        // passive: false is required on both so preventDefault() is allowed
        secondModalEl.addEventListener('wheel', this.wheelForwardHandler, {
            passive: false
        });
        secondModalEl.addEventListener(
            'touchstart',
            this.touchStartHandler as EventListener,
            { passive: true } // touchstart doesn't need preventDefault
        );
        secondModalEl.addEventListener(
            'touchmove',
            this.touchMoveHandler as EventListener,
            { passive: false }
        );
    };

    detachScrollForwarder = () => {
        if (!this.secondModalEl) return;
        if (this.wheelForwardHandler) {
            this.secondModalEl.removeEventListener(
                'wheel',
                this.wheelForwardHandler
            );
        }
        if (this.touchStartHandler) {
            this.secondModalEl.removeEventListener(
                'touchstart',
                this.touchStartHandler as EventListener
            );
        }
        if (this.touchMoveHandler) {
            this.secondModalEl.removeEventListener(
                'touchmove',
                this.touchMoveHandler as EventListener
            );
        }
        this.wheelForwardHandler = null;
        this.touchStartHandler = null;
        this.touchMoveHandler = null;
        this.secondModalEl = null;
    };

    render() {
        if (this.state.renderDelayActive) {
            return null;
        }
        let className = `${this.props.className} ${this.state.classNameAnimation} slide-modal from-left`;
        if (this.props.secondModal === true) {
            className = `${className} second-modal`;
        }
        if (this.props.wideModal === true) {
            className = `${className} wide-modal`;
        }
        if (this.props.secondWideModal === true) {
            className = `${className} second-wide-modal`;
        }
        const backdrop = this.props.backdrop ? this.props.backdrop : true;
        return (
            <Modal
                show={this.props.show}
                onHide={this.props.onHide}
                className={className}
                onExiting={() => {
                    this.setState({ classNameAnimation: '' });
                    this.detachScrollForwarder();
                }}
                onEntering={() => {
                    this.setState({ classNameAnimation: 'after-open' });
                }}
                onEntered={() => {
                    setTimeout(() => {
                        this.setState({
                            classNameAnimation: 'after-open after-entered'
                        });
                        // Attach after animation so .second-modal class is on the wrapper.
                        // A second-wide modal arms only when it is the sole second
                        // modal, so stacks that pair it with a plain second modal
                        // (e.g. edit-install + product search) are untouched.
                        if (
                            this.props.secondModal ||
                            (this.props.secondWideModal &&
                                !document.querySelector('.second-modal.modal'))
                        ) {
                            this.attachScrollForwarder();
                        }
                    }, 200);
                    if (this.props.onEntered) {
                        this.props.onEntered();
                    }
                }}
                container={
                    this.props.container ||
                    document.getElementById('two-pane-layout') ||
                    document.querySelector('.app')
                }
                backdrop={backdrop}
            >
                <Modal.Header>
                    <Modal.Title>{this.props.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{this.props.children}</Modal.Body>

                <Modal.Footer>{this.props.footer}</Modal.Footer>
            </Modal>
        );
    }
}

export default CommonModal;
